﻿// #region: Class definition *********************************************************************/
/**
 * Replaces the browsers native rendering of form validation errors.
 * Fields will display validation errors only when the field is invalid and loses focus,
 * or when the field is invalid and it's parent form is submitted. 
 * When the field regains focus, the validation message will update as the user updates the field, finally removing the error
 * altogether once the validation constraints have been met.
 * 
 * Depends on the validation class.
 */
window.validator = class {

    // #region: Private Fields *******************************************************************/
    #bubble;
    #bubbleTimer;
    #field;
    #hideTimeout;
    #parent;
    #validateOnBlur;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     * @param {HTMLElement} field A form element that this validator will be attached to.
     * @param {String} validatorParent An element selector (related to the field) that the bubble will be attached to.
     * @param {Boolean} validateOnBlur Validate on blur, i.e. user leaves the field (defaults to false).
     * @param {Integer} hideTimeout When set to another value than '0' (default) the bubble will hide after the amount set (milliseconds).
     */
    constructor({ field, validatorParent = null, validateOnBlur = false, hideTimeout = 0 } = {}) {
        // No use when we do not have a field.
        if (!field) {
            return false;
        }

        /**
         * The error bubble to show.
         * @type {HTMLElement}
         */
        this.#bubble = null;

        /**
         * The field that is to be validated.
         * @type {HTMLElement}
         */
        this.#field = field;

        /**
         * Validated on losing focus...thus blur.
         * @type {Boolean}
         */
        this.#validateOnBlur = validateOnBlur;

        /**
         * The amount of milliseconds after the error bubble is hidden, 0 means no hiding.
         * @type {Integer}
         */
        this.#hideTimeout = hideTimeout;

        /**
         * The field element that the bubble will be attached to.
         * First checks the tree upwards (closests), then tries to find the 'parent' by the selector provided in the current form.
         * Defaults to it's closests parent div.
         * @type {HTMLElement}
         */
        this.#parent = validatorParent ?
            (
                field.closest(validatorParent)
                ?? field.closest("form")?.querySelector(validatorParent)
                ?? field.closest("div")
            ) : field.closest("div");

        // Hide default title value if the field supports the validation API.
        this.#field.setAttribute("title", "");

        // Capture invalid event, as we want to override the display of the message.
        this.#field.addEventListener("invalid", e => e.preventDefault(), { capture: true });

        // We always validate on change, but not when explicitly disabled.
        if (!this.#field.hasAttribute("data-novalidate-onchange")) {
            this.#field.addEventListener("change", this.validateHandler, { capture: false });
            this.#field.addEventListener("validateonchange", this.validateHandler, { capture: false });
        }

        // When an element is getting focus.
        this.#field.addEventListener("focus", this.validateFocusHandler, { capture: false });

        // Do we want to validate on blur?
        if (this.#validateOnBlur) {
            this.#field.addEventListener("blur", this.validateHandler, { capture: false });
        }

        // Do we want to validate on input?
        if (this.#field.hasAttribute("data-validate-oninput")) {
            this.#field.addEventListener("input", this.validateHandler, { capture: false });

            // When validating on input, we also validate on blur so any error message does not disappear.
            this.#field.addEventListener("blur", this.validateHandler, { capture: false });
        }

        // A validator can validate through events, direct call and when a form is validated.
        // In the latter case we always want to show bubbles, though individual fields might prefer not showing them initially.
        this.isFormValidation = false;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String}                        The string representation of this class.
     */
    toString() {
        return "[validator]";
    }

    static toString() {
        return "[validator]";
    }

    /** 
     * Identifies the version.
     * @returns {String}                        The version number of this class.
     */
    static version() {
        return "1.0.1";
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles events on the element, removes any error messages present and re-evaluates.
     * @param {Event} event The Event data passed when an event fires.
     */
    validateFocusHandler = (event) => {
        if (this.#field.hasAttribute("readonly")) {
            this.hideError();
        }
        else {
            this.validate({ event });
        }
    }

    /**
     * Handles events on the element, removes any error messages present and re-evaluates.
     * @param {Event} event The Event data passed when an event fires.
     */
    validateHandler = (event) => {
        this.validate({ event });
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Checks if a bubble exists.
     * @returns {Boolean} True when a bubble exists, false if not.
     */
    hasBubble() {
        return !!this.#bubble;
    }

    /**
     * Hide any error indicators.
     * @param {Boolean} group The value is true if this method is called from within a radio/checkbox group 'hideError'
     *                        to prevent recursion and therefor a stack overflow exception.
     */
    hideError(group = false) {
        this.#hideFieldError();

        // If the field is a checkbox or a radio button it might be part of a group.
        // Clear any messages on any item of the group.
        if (!group && (this.#field.type === "radio" || this.#field.type === "checkbox")) {
            // Do we have group items that are invalid.
            let ownerWindow = this.#field.ownerDocument.defaultView;
            let ownerDocument = ownerWindow.document;

            const items = ownerDocument.querySelectorAll(`input[name^="${this.#field.name.split("#")[0]}"]`);

            if (items?.length > 1) {
                [...items].forEach((item) => {
                    item.validator?.hideError(true);
                });
            }
        }
    }

    /**
     * Give the validator (related) elements invalid indicators.
     */
    setInvalid() {
        this.#field.setAttribute("aria-invalid", "true");
        this.#parent?.classList.add("has-invalid-content");
    }

    /**
     * Shows/updates the error bubble when not present.
     * @param {string} message The message to display.
     * @param {HTMLElement} parent Where to display the bubble  (optional).
     * @param {Integer} hideTimeout Hide the bubble after some time, defaults to null.
     *                              Not auto hiding or using the value set from the constructor (optional).
     */
    showError({ message = null, parent = null, hideTimeout = null }) {
        if (message) {
            const ignoreBubble = !this.isFormValidation && this.#field.hasAttribute('data-ignorebubble');

            if (!ignoreBubble) {
                this.#bubble = this.#field.closest('.bubble') ?? this.#parent.querySelector('.bubble');

                if (this.#bubble) {
                    this.#bubble.innerHTML = `<i class="bubble-icon"></i><span>${message}</span><i class="bubble-close"></i>`;
                    this.#bubble.classList.remove('hidden');
                }
                else {
                    // Create bubble.
                    this.#bubble = document.createElement('div');
                    this.#bubble.classList.add('bubble', 'hidden');
                    this.#bubble.innerHTML = `<i class="bubble-icon"></i><span>${message}</span><i class="bubble-close"></i>`;
                    (parent ?? this.#parent)?.append(this.#bubble);
                    this.#bubble.classList.remove('hidden');
                }

                // The 'bubble-close' button hides the error.
                this.#bubble.querySelector('.bubble-close').addEventListener('click', () => {
                    this.hideError();
                }, { capture: false });

                // Is a custom timeout provided? Update the instance setting.
                if (hideTimeout) {
                    this.#hideTimeout = hideTimeout;
                }

                this.setInvalid();

                // Hide after some time?
                if (this.#hideTimeout > 0) {
                    clearTimeout(this.#bubbleTimer);

                    this.#bubbleTimer = setTimeout(() => {
                        return this.hideError();
                    }, this.#hideTimeout);
                }

                // Validation is done, reset the isFormValidation to false again.
                this.isFormValidation = false;
            }
            else {
                this.#field.setAttribute('title', message);
            }

            // When a error is displayed, add the oninput handler so message will updated when the user enters content.
            this.#field.addEventListener('input', this.validateHandler, { capture: false, once: true });
        }
        else {
            console.error(`Parameter 'message' is 'null' on element '${this.#field.name}'.`);
        }
    }

    /**
     * Actually validate the attached field.
     * Shows the error message when invalid.
     * @param {Boolean} required Whether to validate 'required', typically only when called from validation.validateForm.
     * @param {Event} event The generic Event data passed when a change event fires.
     * @returns {Boolean} True when the control validates, otherwise false.
     */
    validate({ required = false, event = null }) {
        let isValid = true;

        if (event?.type === "focus" && !this.#field.value) {
            return true;
        }

        // Do not validate disabled fields, no way the user can fix it.
        if (this.#field && !this.#field.hasAttribute("disabled")) {
            const forceValidate = this.#field.hasAttribute("data-force-validate");

            let ownerWindow = this.#field.ownerDocument.defaultView;
            let ownerDocument = ownerWindow.document;

            // Element implements the validation API.
            if (typeof this.#field.setCustomValidity === "function") {

                // A radiobutton or checkbox might be part of a group and when invalid do not parse validity on every member.
                if (this.#field.type === "radio" || this.#field.type === "checkbox") {
                    // A '#' is used to identify checkboxes in a group name.
                    const groupItems = ownerDocument.querySelectorAll(`input[type="${this.#field.type}"][name^="${this.#field.name.split("#")[0]}"]`);
                    const invalidItemCount = [...groupItems].filter(item => item.hasAttribute('aria-invalid')).length;

                    if (groupItems.length > 1 && invalidItemCount > 1 && invalidItemCount !== groupItems.length) {
                        // We have already validated this element and only want one message per group, so we do not validate any further.
                        return true;
                    }

                    if (this.#field.hasAttribute("required") && required) {
                        isValid = validation.validateGroup({ element: this.#field, items: [...groupItems] });
                    }
                }
                else {
                    // Default contraint API check.
                    isValid = this.#field.validity.valid;

                    // When state is invalid AND we do not want to validate required we need to check if the 'valuemissing' property is
                    // the only one property that is invalid. If so reset isValid property.
                    if (!isValid && !required) {
                        const invalidProperties = this.#getInvalidProperties(this.#field.validity);

                        // When length is '1', only one is invalid.
                        // Check if that's 'valueMissing'.
                        if (invalidProperties?.length === 1 && invalidProperties[0] === "valueMissing") {
                            isValid = true;
                        }
                    }

                    // If it's a file (upload) control, its value(s) are set in a separate 'attachments_' container.
                    // If data was (previously) loaded, check if any preview containers are created.
                    if (this.#field.type === "file" && !isValid) {
                        isValid = ownerDocument.querySelector("#attachments_" + this.#field.name)?.children.length > 0;
                    }
                }

                // Check additional validation when defaults are good (or we want to validate everything).
                if (isValid || forceValidate) {
                    const validateAdditional = validation.validateAdditional({ event: event, element: this.#field });

                    // Do not reset isValid when false.
                    if (isValid) {
                        isValid = validateAdditional;
                    }
                }

                // The HTML5 spec says: “If the readonly attribute is specified on an input element, the element is barred from constraint validation.”
                // So we need to do required validation for readonly fields ourselves.
                if ((isValid || forceValidate) && this.#field.hasAttribute("readonly")) {
                    const validateReadOnly = validation.validateReadOnly({ event: event, element: this.#field });

                    // Do not reset isValid when false.
                    if (isValid) {
                        isValid = validateReadOnly;
                    }
                }
            
                // Custom validators.
                if ((isValid || forceValidate) && this.#field.hasAttribute("data-customvalidator")) {
                    const validationMethod = this.#field.dataset.customvalidator;
                    const targetWindow = typeof ownerWindow[validationMethod] === "function" ? ownerWindow : window;

                    // Only call when function exists.
                    if (typeof targetWindow[validationMethod] === "function") {
                        isValid = targetWindow[validationMethod]({ event: event, element: this.#field });
                    }
                }
            }

            // Check if this is a web component with a validate method.
            if (this.#field.tagName && this.#field.tagName.includes('-') && typeof this.#field.validate === 'function') {
                isValid = this.#field.validate();
            }

            if (isValid) {
                this.hideError();
            }
            else {
                const errorMessage = validation.getMessage(this.#field, true);

                this.showError({ message: errorMessage });

                // Do we want to do something when done? This is set on a control in html so check if the method exists before calling.
                const afterValidate = this.#field.dataset.aftervalidate;
                const targetWindow = typeof ownerWindow[afterValidate] === "function" ? ownerWindow : window;

                if (typeof targetWindow[afterValidate] === "function") {
                    targetWindow[afterValidate]();
                }
            }
        }

        return isValid;
    }
    // #region: Public Methods *******************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Check which ValidityState Instance properties are true, i.e. are invalid.
     * @param {Object} validityState The state object.
     * @returns {Object} The collection of invalid properties.
     */
    #getInvalidProperties(validityState) {
        const invalidProperties = [];

        for (var key in validityState) {
            if (validityState[key]) {
                invalidProperties.push(key);
            }
        }

        return invalidProperties;
    }

    /**
     * Actually hides the error indicators (bubble, invalid and parent-has-invalid-content).
     */
    #hideFieldError() {
        const bubble = this.#bubble ?? this.#field.closest(".bubble") ?? this.#parent?.querySelector(".bubble");
        if (bubble) {
            bubble.parentNode?.removeChild(bubble);
        }

        this.#field.removeAttribute("aria-invalid");
        this.#field.setAttribute("title", "");

        if (this.#field.setCustomValidity) {
            this.#field.setCustomValidity("");
        }

        // Remove 'input' event (when not set as an attribute on the field itself).
        if (!(this.#field.hasAttribute("data-validate-oninput"))) {
            this.#field.removeEventListener("input", this.validateHandler, { capture: false });
        }
        
        // Remove 'invalid' class (and on any parents).
        this.#field.classList.remove("invalid");
        let invalidElement = this.#field.closest(".invalid");

        do {
            invalidElement?.classList.remove("invalid");
            invalidElement = invalidElement?.closest(".invalid");
        }
        while (invalidElement);

        // Remove 'has-invalid-content' class on any parents.
        let invalidContentElement = this.#field.closest(".has-invalid-content");

        do {
            invalidContentElement?.classList.remove("has-invalid-content");
            invalidContentElement = invalidContentElement?.closest(".has-invalid-content");
        }
        while (invalidContentElement);

        // Reset bubble;
        this.#bubble = null;
    }
    // #region: Private Methods ******************************************************************/

};
// #endregion ************************************************************************************/